import { useState } from 'react';
import { Button, Card, Flex, Text, Select } from '@radix-ui/themes';
import { PlusIcon } from '@radix-ui/react-icons';
import {
  SlotDirection,
  SlotType,
  SlotDataType,
  CreateWorkflowNodeSlotInput,
  WorkflowNodeSlot,
  SignalType,
  TransactionField,
  LogField,
} from '@/types';

// BlockField enum (inline for now, should be moved to backend later)
export enum BlockField {
  Hash = 'HASH',
  Number = 'NUMBER',
  Timestamp = 'TIMESTAMP',
  TransactionCount = 'TRANSACTION_COUNT',
  AverageValue = 'AVERAGE_VALUE',
}

// Predefined attribute arrays for each signal type
const transactionAttributes = [
  { key: TransactionField.From, label: 'From', type: SlotDataType.Address },
  { key: TransactionField.To, label: 'To', type: SlotDataType.Address },
  { key: TransactionField.Value, label: 'Value', type: SlotDataType.Number },
  { key: TransactionField.Gas, label: 'Gas', type: SlotDataType.Number },
  {
    key: TransactionField.GasPrice,
    label: 'Gas Price',
    type: SlotDataType.Number,
  },
  {
    key: TransactionField.MaxPriorityFeePerGas,
    label: 'Max Priority Fee Per Gas',
    type: SlotDataType.Number,
  },
  {
    key: TransactionField.MaxFeePerGas,
    label: 'Max Fee Per Gas',
    type: SlotDataType.Number,
  },
  { key: TransactionField.Input, label: 'Input', type: SlotDataType.Bytes },
];

const logAttributes = [
  { key: LogField.Address, label: 'Address', type: SlotDataType.Address },
  { key: LogField.Topic_0, label: 'Topic 0', type: SlotDataType.Topic },
  { key: LogField.Topic_1, label: 'Topic 1', type: SlotDataType.Topic },
  { key: LogField.Topic_2, label: 'Topic 2', type: SlotDataType.Topic },
  { key: LogField.Topic_3, label: 'Topic 3', type: SlotDataType.Topic },
  { key: LogField.Data, label: 'Data', type: SlotDataType.Bytes },
  { key: LogField.BlockHash, label: 'Block Hash', type: SlotDataType.String },
  {
    key: LogField.BlockNumber,
    label: 'Block Number',
    type: SlotDataType.Number,
  },
  {
    key: LogField.TransactionHash,
    label: 'Transaction Hash',
    type: SlotDataType.String,
  },
  {
    key: LogField.TransactionIndex,
    label: 'Transaction Index',
    type: SlotDataType.Number,
  },
  { key: LogField.LogIndex, label: 'Log Index', type: SlotDataType.Number },
];

const blockAttributes = [
  { key: BlockField.Hash, label: 'Hash', type: SlotDataType.String },
  { key: BlockField.Number, label: 'Number', type: SlotDataType.Number },
  { key: BlockField.Timestamp, label: 'Timestamp', type: SlotDataType.Number },
  {
    key: BlockField.TransactionCount,
    label: 'Transaction Count',
    type: SlotDataType.Number,
  },
  {
    key: BlockField.AverageValue,
    label: 'Average Value',
    type: SlotDataType.String,
  },
];

interface SlotSectionProps {
  direction: SlotDirection;
  signalType: SignalType;
  onSave: (data: CreateWorkflowNodeSlotInput) => void;
  label: string;
  existingSlots?: WorkflowNodeSlot[];
}

const SlotSection = ({
  direction,
  signalType,
  onSave,
  label,
  existingSlots = [],
}: SlotSectionProps) => {
  const [isCreating, setIsCreating] = useState(false);
  const [selectedAttribute, setSelectedAttribute] = useState('');
  const [optional, setOptional] = useState(false);

  // Determine slot type based on direction
  const slotType =
    direction === SlotDirection.Input || direction === SlotDirection.Output
      ? SlotType.Variable
      : SlotType.Control;

  const getAttributeArray = () => {
    switch (signalType) {
      case SignalType.Transaction:
        return transactionAttributes;
      case SignalType.Log:
        return logAttributes;
      case SignalType.Block:
        return blockAttributes;
      default:
        return [];
    }
  };

  const allAttributes = getAttributeArray();

  // Filter out attributes that are already configured as outputs
  const availableAttributes = allAttributes.filter((attr) => {
    const isAlreadyConfigured = existingSlots.some(
      (slot) => slot.key === attr.key && slot.direction === direction,
    );
    return !isAlreadyConfigured;
  });

  const selectedAttributeInfo = availableAttributes.find(
    (attr) => attr.key === selectedAttribute,
  );

  // Outputs must have signalType and predefined attributes
  const isOutput = direction === SlotDirection.Output;
  const hasSignalType = !!signalType;
  const hasAvailableAttributes = availableAttributes.length > 0;

  // For outputs, we need both signalType and available attributes
  const shouldShowDropdown = hasSignalType && hasAvailableAttributes;
  const canCreateOutput = isOutput ? shouldShowDropdown : true;

  const handleSave = () => {
    if (!selectedAttribute) return;

    const slotData: CreateWorkflowNodeSlotInput = {
      nodeId: '', // This should be passed from parent or context
      key: selectedAttribute,
      direction,
      slotType,
      dataType: selectedAttributeInfo?.type || SlotDataType.String,
      optional,
    };

    onSave(slotData);
    setIsCreating(false);
    setSelectedAttribute('');
    setOptional(false);
  };

  const handleCancel = () => {
    setIsCreating(false);
    setSelectedAttribute('');
    setOptional(false);
  };

  if (isCreating) {
    return (
      <Card size="1" style={{ border: '1px solid var(--gray-6)' }}>
        <Flex direction="column" gap="2">
          <Text size="1" weight="bold" color="gray">
            {label}
          </Text>

          <Flex direction="column" gap="2">
            {shouldShowDropdown ? (
              <Select.Root
                value={selectedAttribute}
                onValueChange={setSelectedAttribute}
              >
                <Select.Trigger placeholder="Select attribute" />
                <Select.Content>
                  {availableAttributes.map((attr) => (
                    <Select.Item key={attr.key} value={attr.key}>
                      {attr.label}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            ) : (
              <input
                type="text"
                placeholder="Slot key"
                value={selectedAttribute}
                onChange={(e) => setSelectedAttribute(e.target.value)}
                style={{
                  padding: '4px 8px',
                  border: '1px solid var(--gray-6)',
                  borderRadius: '4px',
                  fontSize: '12px',
                  backgroundColor: 'var(--gray-1)',
                  color: 'var(--gray-12)',
                }}
              />
            )}

            {selectedAttributeInfo && (
              <Text size="1" color="gray">
                Type: {selectedAttributeInfo.type}
              </Text>
            )}

            <Flex align="center" gap="2">
              <input
                type="checkbox"
                id="optional"
                checked={optional}
                onChange={(e) => setOptional(e.target.checked)}
              />
              <label htmlFor="optional" style={{ fontSize: '12px' }}>
                Optional
              </label>
            </Flex>

            <Flex gap="2" justify="between">
              <Button size="1" variant="soft" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                size="1"
                onClick={handleSave}
                disabled={!selectedAttribute}
              >
                Save
              </Button>
            </Flex>
          </Flex>
        </Flex>
      </Card>
    );
  }

  return (
    <Button
      variant="ghost"
      size="1"
      color="gray"
      style={{
        border: '1px dashed var(--gray-7)',
        borderRadius: '0.5rem',
        fontWeight: 500,
        width: '100%',
        margin: 0,
        boxSizing: 'border-box',
      }}
      onClick={() => setIsCreating(true)}
      disabled={!canCreateOutput}
    >
      <PlusIcon width={16} height={16} />
      {label}
    </Button>
  );
};

export default SlotSection;
